import { useState } from "react";
import { useDebounce } from "../../../../../hooks/useDebounce";
import LimitSelect from "../../../../elements/LimitSelect";
import Search from "../../../../form/Search";
import Table from "../../../../elements/table/Table";
import { handleFilterChange } from "../../../../../helpers";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { useOrganization } from "../../../../../context/OrganizationContext";
import { exportList } from "../../../../../services/reportService";
import Button from "../../../../form/Button";
import { useOutletContext } from "react-router-dom";
import { t } from "i18next";

const SaleExportsList = () => {
  const { event } = useOutletContext();
  const { selectedOrganization } = useOrganization();
  const [selectedItems, setSelectedItems] = useState([]);
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  // Fetch exports data
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["exports", filters, search, selectedOrganization],
    queryFn: async () => {
      const res = await exportList({
        ...filters,
        ...(search && { keyword: search }),
        type: "sales-summary",
        organization: selectedOrganization,
        event: event?.data?._id,
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!selectedOrganization,
  });

  const getStatusStyle = (status) => {
    const statusLower = status?.toLowerCase();

    const statusStyles = {
      success: {
        backgroundColor: "#D1FAE5",
        color: "#047857",
        borderColor: "#10B981",
      },
      processing: {
        backgroundColor: "#FEF3C7",
        color: "#D97706",
        borderColor: "#F59E0B",
      },
      failed: {
        backgroundColor: "#FEE2E2",
        color: "#DC2626",
        borderColor: "#EF4444",
      },
    };

    return statusStyles[statusLower] || statusStyles.processing;
  };

  // Enhanced download function with multiple fallback methods
  const handleDirectDownload = async (fileUrl, filename) => {
    try {
      if (!fileUrl) {
        toast.error("File URL is not available");
        return;
      }

      // Method 1: Try direct link download
      try {
        const link = document.createElement("a");
        link.href = fileUrl;
        link.download = filename || "export-file.csv";
        link.rel = "noopener noreferrer";
        link.style.display = "none";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Download initiated!");
      } catch (linkError) {
        console.warn("Direct link download failed:", linkError);

        // Method 2: Open in new tab as fallback
        try {
          const newWindow = window.open(
            fileUrl,
            "_blank",
            "noopener,noreferrer"
          );
          if (newWindow) {
            toast.info(
              "File opened in new tab. Please save it manually if the download doesn't start automatically."
            );
          } else {
            throw new Error("Popup blocked");
          }
        } catch (windowError) {
          toast.error(
            "Download failed. Please check if popups are blocked and try again."
          );
        }
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error(`Download failed: ${error.message}`);
    }
  };

  // Enhanced Download Button Component
  const DownloadButton = ({ item }) => {
    const handleDownload = async () => {
      const filename =
        item?.file?.filename ||
        `${item?.type}-export-${dayjs(item?.createdAt).format("YYYY-MM-DD")}`;
      await handleDirectDownload(item?.file?.url, filename);
    };

    return item?.status === "success" ? (
      <Button
        buttonFunction={handleDownload}
        buttonIcon={"fas fa-download"}
        variant="secondary"
        buttonClasses={`w-10 hover:bg-transparent`}
      />
    ) : (
      <span className="text-gray-400" title="File not ready for download">
        -
      </span>
    );
  };

  const tableHeadData = [
    {
      _id: 1,
      name: t("Report Name"),
      align: "left",
    },
    {
      _id: 2,
      name: t("Date Requested"),
      align: "left",
    },
    {
      _id: 3,
      name: t("Status"),
      align: "left",
    },
    {
      _id: 4,
      name: t("Download"),
      align: "left",
    },
  ];

  const tableData = data?.data?.map((item) => {
    const status = item?.status || "processing";
    const statusText = status.charAt(0)?.toUpperCase() + status.slice(1);
    const statusStyle = getStatusStyle(status);

    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[200px] font-medium",
          data: item?.name || `${item?.type?.toUpperCase()} Export`,
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[150px] text-gray-600",
          data: dayjs(item?.createdAt).format("MMM DD, YYYY hh:mm A") || "-",
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border"
              style={{
                backgroundColor: statusStyle.backgroundColor,
                color: statusStyle.color,
                borderColor: statusStyle.borderColor,
              }}
            >
              {status === "processing" && (
                <svg
                  className="animate-spin -ml-1 mr-2 h-3 w-3"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {statusText}
            </span>
          ),
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: <DownloadButton item={item} />,
        },
      ],
    };
  });

  return (
    <div className="space-y-4">
      {/* Search and Controls */}
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-full md:w-36">
            <LimitSelect
              value={filters?.limit}
              allValue={data?.metadata?.totalDocs}
              onChange={(val) => handleFilterChange("limit", val, setFilters)}
            />
          </div>
          <div className="w-full md:w-72">
            <Search
              placeholder={t("Search here")}
              search={(val) => {
                setKeyword(val);
                handleFilterChange("page", 1, setFilters);
              }}
            />
          </div>
        </div>
      </div>

      {/* Exports Table */}
      <Table
        tableHeadData={tableHeadData}
        tableData={tableData}
        isLoder={isLoading}
        pageCount={data?.metadata?.totalPages}
        currentPage={filters?.page}
        onPageChange={(val) => {
          handleFilterChange("page", val, setFilters);
          setSelectedItems([]);
        }}
        originalData={[...(data?.data || [])]}
        setOriginalData={(val) => {
          queryClient.setQueryData(
            ["exports", filters, search, selectedOrganization],
            (state) => {
              if (state) {
                return { ...state, data: val };
              }
            }
          );
        }}
        isFetching={isFetching}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        tableActionDropdownContainer={"!w-48"}
        emptyStateText="No exports found"
        emptyStateSubtext="Create an export to see it here"
      />
    </div>
  );
};

export default SaleExportsList;
